import { ConfirmContent } from "../api/Items";
import {
  AcademicCapIcon,
  AtSymbolIcon,
  BriefcaseIcon,
  CalendarIcon,
  EnvelopeIcon,
  HomeIcon,
  PhoneIcon,
  PhotoIcon,
  UserIcon,
} from "@heroicons/react/24/outline";

type ProfileConfirmEntityIcon = typeof HomeIcon;

export type ProfileConfirmEntityId =
  | "address"
  | "job_history"
  | "date_of_birth"
  | "name"
  | "education"
  | "phone"
  | "profile_picture"
  | "handle"
  | "email"
  | "other";

export type ProfileAddConfirmation = {
  entities: ProfileConfirmEntityId[];
  otherText?: string;
};

export const OTHER_ENTITY_MAX_LENGTH = 255;

export type ProfileConfirmEntityOption = {
  id: ProfileConfirmEntityId;
  label: string;
  icon?: ProfileConfirmEntityIcon;
};

export const PROFILE_CONFIRM_ENTITIES: ProfileConfirmEntityOption[] = [
  { id: "address", label: "Address", icon: HomeIcon },
  { id: "job_history", label: "Job History", icon: BriefcaseIcon },
  { id: "date_of_birth", label: "Date of Birth", icon: CalendarIcon },
  { id: "name", label: "Name", icon: UserIcon },
  { id: "education", label: "Education", icon: AcademicCapIcon },
  { id: "phone", label: "Phone", icon: PhoneIcon },
  { id: "profile_picture", label: "Profile Picture", icon: PhotoIcon },
  { id: "handle", label: "Handle", icon: AtSymbolIcon },
  { id: "email", label: "Email", icon: EnvelopeIcon },
  { id: "other", label: "Other" },
];

// Maps Vetric discovery_matches sources onto confirm-entity checkbox IDs.
// Unmapped sources (existing, cross_platform_*, link_match, search, etc.) are
// ignored; profile_picture / other are never auto-selected.
const DISCOVERY_SOURCE_TO_CONFIRM_ENTITY: Record<
  string,
  ProfileConfirmEntityId
> = {
  name: "name",
  handle: "handle",
  handle_fuzzy: "handle",
  email: "email",
  phone: "phone",
  birthdate: "date_of_birth",
  age: "date_of_birth",
  location: "address",
  education: "education",
  job: "job_history",
};

export function mapDiscoveryMatchesToConfirmEntities(
  matches?: { source: string }[]
): ProfileConfirmEntityId[] {
  if (!Array.isArray(matches) || matches.length === 0) {
    return [];
  }

  const selected = new Set<ProfileConfirmEntityId>();
  for (const match of matches) {
    const entityId = DISCOVERY_SOURCE_TO_CONFIRM_ENTITY[match.source];
    if (entityId) {
      selected.add(entityId);
    }
  }

  return PROFILE_CONFIRM_ENTITIES.map(({ id }) => id).filter((id) =>
    selected.has(id)
  );
}

export function isProfileAddConfirmationValid(
  selection: ProfileAddConfirmation
): boolean {
  if (selection.entities.length === 0) {
    return false;
  }

  if (selection.entities.includes("other")) {
    const trimmed = selection.otherText?.trim() ?? "";
    if (!trimmed || trimmed.length > OTHER_ENTITY_MAX_LENGTH) {
      return false;
    }
  }

  return true;
}

function mergeProfileConfirmEntities(
  existingEntities: string[] | undefined,
  selectedEntities: ProfileConfirmEntityId[]
): ProfileConfirmEntityId[] {
  const merged = [...(existingEntities ?? [])];
  const seen = new Set(merged);

  for (const entity of selectedEntities) {
    if (!seen.has(entity)) {
      merged.push(entity);
      seen.add(entity);
    }
  }

  return merged as ProfileConfirmEntityId[];
}

function stripUrlQuery(url: unknown): unknown {
  if (typeof url !== "string") {
    return url;
  }
  const queryStart = url.indexOf("?");
  return queryStart === -1 ? url : url.slice(0, queryStart);
}

// The confirm payload replaces the item's content wholesale, and we spread the
// server-served content back in. That content carries transient S3 *presigned* URLs
// (item-service signs `internal_url`/`media_urls` on every read). Persisting those signed
// URLs poisons the row: on the next read item-service derives the S3 object key from the
// path and the leftover `?X-Amz-...` becomes part of the key, so S3 returns NoSuchKey/403
// and the profile picture disappears (notably for IG/FB, the only sources with an
// internal_url). Strip the query so we store the clean object path; `external_url` (the
// original CDN link) is left untouched since it is not re-signed.
function sanitizePersistedMedia(content: ConfirmContent): ConfirmContent {
  const sanitized: ConfirmContent = { ...content };

  if (Array.isArray(sanitized.media)) {
    sanitized.media = sanitized.media.map((entry) => {
      if (!entry || typeof entry !== "object") {
        return entry;
      }
      if (typeof entry.internal_url === "string") {
        return { ...entry, internal_url: stripUrlQuery(entry.internal_url) };
      }
      return entry;
    });
  }

  if (Array.isArray(sanitized.media_urls)) {
    sanitized.media_urls = sanitized.media_urls.map(stripUrlQuery);
  }

  return sanitized;
}

export function buildProfileConfirmPayload(
  confirmation?: ProfileAddConfirmation,
  existingContent?: ConfirmContent
): ConfirmContent | undefined {
  if (!confirmation) {
    return undefined;
  }

  const payload: ConfirmContent = {
    ...(existingContent ? sanitizePersistedMedia(existingContent) : {}),
    profile_confirm_entities: mergeProfileConfirmEntities(
      existingContent?.profile_confirm_entities,
      confirmation.entities
    ),
  };

  if (confirmation.entities.includes("other")) {
    const otherText = confirmation.otherText?.trim();
    if (otherText) {
      payload.profile_confirm_other_text = otherText;
    }
  }

  return payload;
}
